import { FC, useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../../../components/shared/Modal/Modal";
import styles from "./MassMessagingModal.module.scss";
import { getSupplierToMassMessageTo, sendMassMessage } from '../../../services/massMessageService';
import Editor from "../../../components/shared/Editor/Editor";
import appState from "../../../state/AppStateContainer";
import { NotificationManager } from "react-notifications";
import * as Sentry from "@sentry/react";
import { format } from "date-fns";
import { serviceOrganization, } from "../../../services/service-organization";
import FileUpload, { ExistingFile, NewFile } from '../../../components/shared/FileUpload/FileUpload';
import { useTranslation, Trans } from "react-i18next";
import MassMessagingRecipientSelector from "./MassMessagingRecipientSelector";
import _ from "lodash";

export type Supplier = {
  supplierId: string,
  supplierName: string,
  won: boolean,
  intent: 'DECLINED' | 'ACCEPTED',
  hasBid: boolean,
  contacts: Contact[],
}

export type Purchase = {
  areaId: string,
  areaName: string,
  suppliers: Supplier[],
}

export type Contact = {
  id: string,
  name: string,
  email: string,
  title: string,
}

type Props = {
  projectId: string,
}

export type AreaSelectionType = boolean | { [x: string]: SupplierSelectionType};
export type SupplierSelectionType = boolean | { [x: string]: ContactSelectionType};
export type ContactSelectionType = boolean;

export type RecipientListType = { contact: Contact; projectId: string; areaId: string; areaName: string; supplierId: string; }[];


export const MassMessagingModal: FC<Props> = ({ projectId, }) => {
  const [t] = useTranslation('projects', { keyPrefix: 'massMessaging' });
  const [message, setMessage] = useState('');
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [recipientSelection, setRecipientSelection] = useState<{[x: string]: AreaSelectionType}>({});
  const [shownRecipients, setShownRecipients] = useState<string[]>([]);
  const showMassMessagingModal = appState.getShowMassMessagingModal();
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [massMessagingOrgInfo, setMassMessagingOrgInfo] = useState<{singleMessageLimit: number, weeklyMessagesLimit: number, messagesSentWeekly: {[x: string]: {amount: number, limit: number}}}>();
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
  const [newFiles, setNewFiles] = useState<NewFile[]>([]);

  const SINGLE_LIMIT = massMessagingOrgInfo?.singleMessageLimit || 200;
  const WEEKLY_LIMIT = massMessagingOrgInfo?.weeklyMessagesLimit || 600;
  const weeklyInfo = massMessagingOrgInfo?.messagesSentWeekly;
  const infoThisWeek = weeklyInfo?.[format(new Date(), 'YYYY-ww', {useAdditionalWeekYearTokens: true,})] || {amount: 0, limit: WEEKLY_LIMIT};

  const resetModal = useCallback(() => {
    setMessage('');
    setPurchases([]);
    setRecipientSelection({});
    setShownRecipients([]);
    setNewFiles([]);
    setExistingFiles([]);
  }, []);

  const setShowMassMessagingModal = useCallback((value: boolean) => {
    appState.setShowMassMessagingModal(value);
  }, []);

  useEffect(() => {
    if (showMassMessagingModal) {
      resetModal();
      (async () => {
        setLoading(true);
        const purchases = await getSupplierToMassMessageTo(projectId);
        setPurchases(purchases);
        const massMessagingOrgInfo = await serviceOrganization.getField('massMessaging');
        setMassMessagingOrgInfo(massMessagingOrgInfo);
        setLoading(false);
      })();
    }
  }, [showMassMessagingModal]);

  const getContacts = useCallback((areaId: string, suppliers: Supplier[]) => {
    const purchase = purchases.find(purchase => purchase.areaId === areaId);
    if (purchase) {
      return suppliers.reduce((acc: RecipientListType, current) => ([...acc, ...current.contacts.map(c => ({contact: c, projectId, areaId, areaName: purchase.areaName, supplierId: current.supplierId}))]),[]);
    }
    return [];
  }, [purchases, projectId]);

  const recipientList = useMemo(() => Object.entries(recipientSelection).reduce((acc: RecipientListType, [key, value]) => {
    if (value === true) {
      const purchase = purchases.find(purchase => purchase.areaId === key);
      if (purchase) {
        const suppliers = getContacts(purchase.areaId, purchase.suppliers);
        return [...acc, ...suppliers];
      }
      return acc;
    } else {
      const purchase = purchases.find(purchase => purchase.areaId === key);
      if (purchase) {
        const area = Object.entries(value);
        const suppliers = getContacts(purchase.areaId, area.filter(([_, sup]) => sup === true).map(([supId, _]) => purchase.suppliers.find(y => y.supplierId === supId)).filter(x => !!x));
        const areasWithIndividualSelections = area.filter(([_, sup]) => sup !== true);
        const subTotal = areasWithIndividualSelections.reduce(
          (acc: RecipientListType, [supId, sup]) =>
            ([...acc, ...Object.entries(sup).filter(
              ([_, con]) => con === true
            ).map(
              ([conId]) => {
                const supplier = purchase.suppliers.find(y => y.supplierId === supId);
                if (supplier) {
                  const contact = supplier?.contacts.find(contact => contact.id === conId);
                  if (contact) {
                    return {contact, projectId, areaId: purchase.areaId, areaName: purchase.areaName, supplierId: supId};
                  }
                }

              }).filter(x => !!x)]), []);

        return [...acc, ...suppliers, ...subTotal];
      }
      return acc;
    }
  }, []), [recipientSelection, getContacts, purchases, projectId]);



  const sendMessage = useCallback(async () => {
    setSendingMessage(true);
    try {
      const list = recipientList.map(x => ({
        projectId: x.projectId,
        areaId: x.areaId,
        supplierId: x.supplierId,
        supplierMemberId: x.contact.id
      }));

      await sendMassMessage(appState.getCurrentOrgSlug(), list, message, newFiles.map(f => f.file));

      setMessage('');
      setExistingFiles([]);
      setNewFiles([]);
      setShowMassMessagingModal(false);

      NotificationManager.success(
        t('notification.success.description', 'Skickat meddelande'),
        t('notification.success.title', 'Skickat')
      );
    } catch (error) {
      const errorMsg = _.get(error, 'response.data.error', t('notification.error.unexpected.description', 'Oväntat fel ({{error}})', { error }));
      NotificationManager.error(errorMsg, t('notification.error.unexpected.title', 'Misslyckat'));
      Sentry.captureException(error);
    } finally {
      setSendingMessage(false);
    }
  }, [recipientList, message, existingFiles, newFiles]);

  const aboveLimitErrorMessage = useMemo(() => {
    if (recipientList.length + infoThisWeek.amount > WEEKLY_LIMIT) {
      return (
        <div style={{ fontSize: '12px' }}>
          <Trans t={t} i18nKey="errorMessage.aboveWeeklyLimit" values={{ count: recipientList.length + infoThisWeek.amount - WEEKLY_LIMIT, limit: WEEKLY_LIMIT }} components={{b: <b />}} />
        </div>
      );
    } else if (recipientList.length > SINGLE_LIMIT) {
      return (
        <div style={{ fontSize: '12px' }}>
          <Trans t={t} i18nKey="errorMessage.aboveSingleLimit" values={{ limit: SINGLE_LIMIT }} components={{b: <b />}} />
        </div>
      );
    }
  }, [recipientList, infoThisWeek.amount]);

  const recipientSelector = useMemo(() => 
    <MassMessagingRecipientSelector 
      recipientSelection={recipientSelection}
      setRecipientSelection={setRecipientSelection}
      shownRecipients={shownRecipients}
      setShownRecipients={setShownRecipients}
      purchases={purchases}
      aboveLimitErrorMessage={aboveLimitErrorMessage}
      massMessagingOrgInfo={massMessagingOrgInfo}
      loading={loading}
      recipientList={recipientList}
    />, 
    [recipientSelection, setRecipientSelection, shownRecipients, setShownRecipients, purchases, aboveLimitErrorMessage, massMessagingOrgInfo, loading, recipientList]);
          
  return (
    <Modal
      title={t('heading', 'Massutskick')}
      show={showMassMessagingModal}
      setShow={(value) => setShowMassMessagingModal(value)}
      hasCloseButton
      buttonInfo={{
        label: t('button.send', 'Skicka'),
        action: sendMessage,
        disabled: !message || recipientList.length === 0 || !!aboveLimitErrorMessage,
        loaderShow: sendingMessage,
      }}
    >
      <div className={styles.massMessagingModal}>
        {recipientSelector}
        <Editor
          value={message}
          onChange={(value) => setMessage(value)}
          wrapperStyle={{ height: '291px' }}
        />
        <div style={{ marginTop: '16px' }}>
          <FileUpload
            existingFiles={existingFiles}
            setExistingFiles={setExistingFiles}
            newFiles={newFiles}
            setNewFiles={setNewFiles}
            inlineButton={false}
            hasRemoveAllButton={true}
          />
        </div>
      </div>
    </Modal>
  )
}
